import { useEffect, useMemo, useRef, useState } from "react";
import Alert, { AlertState } from "@components/common/Alert";
import ParticlesBackground from "@components/common/ParticlesBackground";
import { ReactComponent as LoadingSpinner } from "@images/loading_spinner.svg";
import { useOnboardingProfile } from "@providers/OnboardingContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as SearchIcon } from "@images/search_icon.svg";
import ExpandableGroupRow from "./ExpandableGroupRow";
import { NLSenderProspect } from "@models/Models";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";
import * as analyticsManager from "@utils/managers/AnalyticsManager";

const Organized = (props) => {
	const { onboardingProfile, setOnboardingProfile } = useOnboardingProfile();
	const location = useLocation();
	const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
    const searchBoxRef = useRef<HTMLInputElement>(null);
	const [isLoading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		analyticsManager.recordEvent(kAnalyticsConstants.Onboarding.organizedPresented);
	}, []);

	const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});

	const groupedProspects: Record<string, NLSenderProspect[]> = useMemo(() => {
		const result: Record<string, NLSenderProspect[]> = {};
		const selectedSenderIds = new Set<number>(onboardingProfile?.persistence.feedArray || []);
		const allProspects: NLSenderProspect[] = onboardingProfile?.persistence.prospectArray || [];
		for (const p of allProspects) {
			if (!selectedSenderIds.has(p.sender_id)) continue;
			for (const groupName of p.sender_groups || []) {
				if (!result[groupName]) result[groupName] = [];
				result[groupName].push(p);
			}
		}

		return result;
	}, [onboardingProfile]);

	return (
		<div>
			<Alert alertState={alertState} setAlertState={setAlertState} />
			<ParticlesBackground />
			<div className={`flex min-h-[100dvh] flex-col justify-center py-12`}>
				<div className="fixed top-0 bg-black/50 w-full p-3 z-30 backdrop-blur-lg border-b border-white/10 shadow-2xl">
					<div className="max-w-[1080px] w-full z-30 m-auto">
						<div className="flex justify-end items-center">
							<Link to="/get-started/discover">
								<button className={`text-white font-medium font-primary text-lg focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-success-green/80 hover:bg-success-green/100 transition ease-in-out whitespace-nowrap opacity-100 cursor-pointer"}`}>Next</button>
							</Link>
						</div>
					</div>
				</div>
				<div className="flex flex-col gap-10 items-center justify-center p-3 mt-[50px] md:mt-[80px] animate-subtleFadeIn">
					<div className="meco_container max-w-[1080px] py-[40px] md:p-[60px] md:pb-[40px]">
						<div className="flex flex-col md:flex-row gap-5 items-start">
							<div className="sticky md:top-[90px] w-full md:w-1/2 flex flex-col gap-2">
								<div className="text-white font-bold font-primary text-4xl leading-10">Your newsletters, organized</div>
								<div className="text-force-primary-light font-medium font-primary text-base  md:text-xl">We've organized your newsletters by topic automatically. Browse by category when you want, or customize the groups in Settings.</div>
							</div>
							<div className="w-full md:w-1/2 flex-col justify-center gap-1 max-w-[450px] md:max-w-none m-auto md:min-w-[400px] flex">
								{Object.entries(groupedProspects).map(([groupName, prospects]) => (
									<ExpandableGroupRow key={groupName} groupName={groupName} prospects={prospects} />
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Organized;
